import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { DEMO_CUSTOMERS } from '../demoData';
import { formatDate } from '../utils/formatDate';
import { COUNTRY_DIAL_CODES, parsePhone, getCountryCodeFromTimezone } from '../utils/phoneCountry';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

// Default the phone country to the one the browser's timezone belongs to, so a
// customer added in India pre-selects +91 rather than always defaulting to +1.
function defaultPhoneCountry() {
  try {
    return getCountryCodeFromTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone).country;
  } catch {
    return 'United States';
  }
}

function computeRowErrors(row) {
  const errors = {};
  const hasEmail = row.email?.trim();
  const hasPhone = row.phone?.trim();
  if (!hasEmail && !hasPhone) {
    errors.general = 'At least an email or phone is required';
  }
  if (hasEmail && !EMAIL_REGEX.test(row.email.trim())) {
    errors.email = 'Invalid email address';
  }
  if (hasPhone) {
    const digits = row.phone.replace(/\D/g, '');
    if (digits.length < 6 || digits.length > 15) {
      errors.phone = 'Phone must be 6–15 digits';
    }
  }
  return errors;
}

const TEMPLATE_CSV = `name,email,phone
Sarah Johnson,sarah.j@example.com,+15551234567
Michael Chen,michael.c@example.com,+15559876543
Emily Rodriguez,,+15558765432
David Kim,david.k@example.com,+15554567890`;

const SAMPLE_ROWS = [
  { name: 'Sarah Johnson', email: 'sarah.j@example.com', phone: '+15551234567' },
  { name: 'Michael Chen', email: 'michael.c@example.com', phone: '+15559876543' },
  { name: 'Emily Rodriguez', email: '', phone: '+15558765432' },
];

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'loopvisitor_customers_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function CsvGuide() {
  const [animStep, setAnimStep] = useState(0);
  const [visibleRows, setVisibleRows] = useState(0);
  const [phase, setPhase] = useState('typing');

  useEffect(() => {
    if (phase === 'typing') {
      if (visibleRows < SAMPLE_ROWS.length) {
        const t = setTimeout(() => setVisibleRows((v) => v + 1), 800);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => { setPhase('done'); setAnimStep(1); }, 1200);
      return () => clearTimeout(t);
    }
    if (phase === 'done' && animStep < 3) {
      const t = setTimeout(() => setAnimStep((s) => s + 1), 2000);
      return () => clearTimeout(t);
    }
    if (animStep >= 3) {
      const t = setTimeout(() => { setPhase('typing'); setVisibleRows(0); setAnimStep(0); }, 3000);
      return () => clearTimeout(t);
    }
  }, [phase, visibleRows, animStep]);

  const steps = [
    { num: '1', icon: '📄', title: 'Download template', desc: 'Get the sample CSV with the right columns' },
    { num: '2', icon: '✏️', title: 'Add your customers', desc: 'Include at least email or phone per row — all other fields are optional' },
    { num: '3', icon: '📤', title: 'Upload & go', desc: 'Click Upload CSV and LoopVisitor handles the rest' },
  ];

  return (
    <div className="csv-guide">
      <div className="csv-guide-left">
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Import customers in seconds</h3>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.6 }}>
          Download our template, paste your customer data, and upload. That's it.
        </p>

        <div className="csv-steps">
          {steps.map((s, i) => (
            <div key={i} className={`csv-step ${animStep >= i ? 'active' : ''}`}>
              <div className="csv-step-icon">{s.icon}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 24 }}>
          <button className="btn btn-primary btn-sm" onClick={downloadTemplate}>
            ⬇ Download Template
          </button>
        </div>
      </div>

      <div className="csv-guide-right">
        <div className="csv-preview-card">
          <div className="csv-preview-header">
            <div className="csv-preview-dots"><span /><span /><span /></div>
            <span>loopvisitor_customers_template.csv</span>
          </div>
          <div className="csv-preview-body">
            <div className="csv-row csv-row-header">
              <span>name</span><span>email</span><span>phone</span>
            </div>
            {SAMPLE_ROWS.map((row, i) => (
              <div
                key={i}
                className={`csv-row csv-row-data ${i < visibleRows ? 'visible' : ''}`}
              >
                <span style={{ color: row.name ? 'inherit' : 'var(--text-muted)' }}>{row.name || '(opt.)'}</span>
                <span style={{ color: row.email ? 'inherit' : 'var(--text-muted)' }}>{row.email || '(opt.)'}</span>
                <span style={{ color: row.phone ? 'inherit' : 'var(--text-muted)' }}>{row.phone || '(opt.)'}</span>
              </div>
            ))}
            {visibleRows >= SAMPLE_ROWS.length && (
              <div className="csv-row-cursor">
                <span className="csv-cursor-blink">|</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h2>{title}</h2>
        <p style={{ color: 'var(--text-secondary)', margin: '12px 0 24px', lineHeight: 1.6 }}>{message}</p>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}


export default function Customers() {
  const { isDemo, user } = useAuth();
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phoneCountryCode: defaultPhoneCountry(), phoneNumber: '' });
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const fileRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [editCustomer, setEditCustomer] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [invalidRows, setInvalidRows] = useState([]);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [editableInvalidRows, setEditableInvalidRows] = useState([]);
  const [importingFixed, setImportingFixed] = useState(false);

  const debounceRef = useRef(null);

  function initEditableRows(rows) {
    return rows.map((row) => ({
      name: row.name || '',
      email: row.email || '',
      phone: row.phone || '',
      errors: computeRowErrors({ email: row.email || '', phone: row.phone || '' }),
    }));
  }

  function updateInvalidRow(i, field, value) {
    setEditableInvalidRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== i) return row;
        const updated = { ...row, [field]: value };
        updated.errors = computeRowErrors(updated);
        return updated;
      })
    );
  }

  function deleteInvalidRow(i) {
    setEditableInvalidRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  const allFixedRowsValid =
    editableInvalidRows.length > 0 &&
    editableInvalidRows.every((row) => Object.keys(row.errors).length === 0);

  async function handleImportFixed() {
    setImportingFixed(true);
    let imported = 0;
    let failed = 0;
    for (const row of editableInvalidRows) {
      try {
        await api.addCustomer({
          name: row.name.trim() || undefined,
          email: row.email.trim() || undefined,
          phone: row.phone.trim() || undefined,
        });
        imported++;
      } catch (err) {
        if (err.message === 'Customer already exists') {
          imported++;
        } else {
          failed++;
        }
      }
    }
    setImportingFixed(false);
    setShowInvalidModal(false);
    if (imported > 0) {
      toast.success(`${imported} customer${imported !== 1 ? 's' : ''} imported`);
      load();
    }
    if (failed > 0) {
      toast.error(`${failed} row${failed !== 1 ? 's' : ''} failed to import`);
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  const load = useCallback(() => {
    if (isDemo) {
      let filtered = DEMO_CUSTOMERS;
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        filtered = DEMO_CUSTOMERS.filter(
          (c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s)
        );
      }
      setCustomers(filtered);
      setTotal(filtered.length);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .getCustomers({ page, search: debouncedSearch })
      .then((d) => {
        setCustomers(d.customers);
        setTotalPages(d.totalPages || 1);
        setTotal(d.total || d.customers.length);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isDemo, page, debouncedSearch]);

  useEffect(load, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isDemo) {
      toast.success('10 customers imported');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const result = await api.uploadCSV(file);
      if (result.limitReached) {
        toast.error(result.message, { persist: result.count === 0 });
      }
      if (result.invalidRows?.length > 0) {
        setInvalidRows(result.invalidRows);
        setEditableInvalidRows(initEditableRows(result.invalidRows));
        toast.success(
          <span style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span>{result.count} customer{result.count !== 1 ? 's' : ''} imported</span>
            <button
              onClick={() => setShowInvalidModal(true)}
              style={{ alignSelf: 'flex-start', background: '#6C5CE7', border: 'none', color: '#fff', cursor: 'pointer', padding: '5px 12px', fontWeight: 600, fontSize: '12px', borderRadius: '6px', lineHeight: 1.4 }}
            >
              View invalid rows
            </button>
          </span>,
          { persist: true }
        );
      } else if (!result.limitReached) {
        toast.success(`${result.count} customer${result.count !== 1 ? 's' : ''} imported`);
      }
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');

    const hasEmail = form.email.trim() !== '';
    const hasPhone = form.phoneNumber.trim() !== '';

    if (!hasEmail && !hasPhone) {
      setFormError('Please enter at least an email or phone number');
      return;
    }

    if (hasEmail && !EMAIL_REGEX.test(form.email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    const digits = form.phoneNumber.replace(/\D/g, '');
    if (hasPhone && (digits.length < 6 || digits.length > 15)) {
      setFormError('Phone number must be between 6 and 15 digits');
      return;
    }

    const countryEntry = COUNTRY_DIAL_CODES.find(c => c.country === form.phoneCountryCode);
    const phoneCode = countryEntry ? countryEntry.code : '+1';
    const phone = hasPhone ? `${phoneCode}${digits}` : '';
    // lastVisitDate is intentionally omitted: it is backend-controlled (set to
    // the creation timestamp) and can never be supplied from the client.
    const payload = { name: form.name || undefined, email: form.email || undefined, phone: phone || undefined };

    const closeModal = () => {
      setShowAdd(false);
      setEditCustomer(null);
      setFormError('');
      setForm((prev) => ({ name: '', email: '', phoneCountryCode: prev.phoneCountryCode, phoneNumber: '' }));
    };

    if (editCustomer) {
      if (isDemo) {
        setCustomers((prev) =>
          prev.map((c) =>
            c.id === editCustomer.id
              ? { ...c, name: form.name || null, email: form.email || null, phone: phone || null }
              : c
          )
        );
        closeModal();
        toast.success('Customer updated successfully');
        return;
      }
      setFormSaving(true);
      try {
        const result = await api.updateCustomer(editCustomer.id, payload);
        setCustomers((prev) => prev.map((c) => (c.id === editCustomer.id ? result.customer : c)));
        closeModal();
        toast.success('Customer updated successfully');
      } catch (err) {
        setFormError(err.message);
      } finally {
        setFormSaving(false);
      }
      return;
    }

    if (isDemo) {
      const now = new Date().toISOString();
      const newCustomer = {
        id: Date.now(),
        name: form.name || null,
        email: form.email || null,
        phone: phone || null,
        lastVisitDate: now,
        createdAt: now,
      };
      setCustomers((prev) => [newCustomer, ...prev]);
      closeModal();
      toast.success('Customer added successfully');
      return;
    }

    try {
      await api.addCustomer(payload);
      closeModal();
      toast.success('Customer added successfully');
      load();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);

    if (isDemo) {
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      return;
    }

    try {
      await api.deleteCustomer(id);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      toast.success('Customer deleted successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };


  return (
    <div>
      <div className="page-header">
        <h1>Customers</h1>
        <p>Manage your customer list and import data</p>
      </div>

      <div className="actions-bar">
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          + Add Customer
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>
            ⬇ Template CSV
          </button>
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            {uploading ? <span className="spinner" /> : '📁 Upload CSV'}
            <input
              type="file"
              accept=".csv"
              onChange={handleUpload}
              ref={fileRef}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

        <div style={{ marginBottom: 16, position: 'relative' }}>
          <input
            type="text"
            placeholder="Search customers by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              paddingRight: searchTerm ? 40 : 14,
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 14,
              outline: 'none',
              background: 'var(--bg)',
              color: 'var(--text-primary)',
              boxSizing: 'border-box',
            }}
          />
          {searchTerm && (
            <button
              type="button"
              title="Clear search"
              aria-label="Clear search"
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, padding: 0, border: 'none', borderRadius: 6,
                background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)',
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover, #f0f0f0)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {!loading && customers.length === 0 && !debouncedSearch && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="empty-state" style={{ padding: '32px 24px' }}>
            <div className="empty-icon">👥</div>
            <h3>No customers yet</h3>
            <p>Add customers manually or upload a CSV to get started.</p>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="modal-overlay" onClick={() => { setShowAdd(false); setEditCustomer(null); setFormError(''); setForm((prev) => ({ name: '', email: '', phoneCountryCode: prev.phoneCountryCode, phoneNumber: '' })); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
            <form onSubmit={handleAdd}>
              {formError && (
                <div className="alert alert-error" style={{ marginBottom: 12 }}>{formError}</div>
              )}
              <div className="form-group">
                <label>Name <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}>(Optional)</span></label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="form-group">
                <label>Email <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}>(Optional)</span></label>
                <input
                  type="text"
                  value={form.email}
                  onChange={(e) => { setForm({ ...form, email: e.target.value }); setFormError(''); }}
                  placeholder="jane@example.com"
                />
              </div>
              <div className="form-group">
                <label>Phone <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}>(Optional)</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={form.phoneCountryCode}
                    onChange={(e) => setForm({ ...form, phoneCountryCode: e.target.value })}
                    style={{
                      flexShrink: 0,
                      width: 90,
                      padding: '8px 4px',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 13,
                      background: 'var(--bg)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {COUNTRY_DIAL_CODES.map((c) => (
                      <option key={c.country} value={c.country} title={c.country}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(e) => { setForm({ ...form, phoneNumber: e.target.value }); setFormError(''); }}
                    placeholder="555 987 6543"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" disabled={formSaving} onClick={() => { setShowAdd(false); setEditCustomer(null); setFormError(''); setForm((prev) => ({ name: '', email: '', phoneCountryCode: prev.phoneCountryCode, phoneNumber: '' })); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formSaving}>
                  {formSaving ? <span className="spinner" /> : editCustomer ? 'Save' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Customer"
          message={`Are you sure you want to delete ${deleteTarget.name || 'this customer'}? This action cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {showInvalidModal && (
        <div className="modal-overlay" onClick={() => setShowInvalidModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h2 style={{ margin: 0 }}>Invalid Rows ({editableInvalidRows.length})</h2>
              <button
                onClick={() => setShowInvalidModal(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 22, lineHeight: 1,
                  padding: '4px 6px', borderRadius: 6, display: 'flex', alignItems: 'center',
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
              Edit the rows below to fix the errors, then import them.
            </p>

            {editableInvalidRows.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                All rows have been removed.
              </p>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Name', 'Email', 'Phone', ''].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left', padding: '8px 10px',
                            borderBottom: '1px solid var(--border)',
                            color: 'var(--text-muted)', fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {editableInvalidRows.map((row, i) => {
                      const { errors } = row;
                      const hasError = errors.email || errors.phone || errors.general;
                      const inputBase = {
                        width: '100%', padding: '6px 8px', fontSize: 13,
                        border: '1px solid var(--border)', borderRadius: 6,
                        background: 'var(--bg)', color: 'var(--text-primary)',
                        outline: 'none', boxSizing: 'border-box',
                      };
                      return (
                        <React.Fragment key={i}>
                          <tr style={{ borderBottom: hasError ? 'none' : '1px solid var(--border)' }}>
                            <td style={{ padding: '8px 10px', minWidth: 130 }}>
                              <input
                                value={row.name}
                                onChange={(e) => updateInvalidRow(i, 'name', e.target.value)}
                                placeholder="Name"
                                style={inputBase}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', minWidth: 180 }}>
                              <input
                                value={row.email}
                                onChange={(e) => updateInvalidRow(i, 'email', e.target.value)}
                                placeholder="email@example.com"
                                style={{
                                  ...inputBase,
                                  borderColor: errors.email ? '#FCA5A5' : 'var(--border)',
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', minWidth: 150 }}>
                              <input
                                value={row.phone}
                                onChange={(e) => updateInvalidRow(i, 'phone', e.target.value)}
                                placeholder="+15551234567"
                                style={{
                                  ...inputBase,
                                  borderColor: errors.phone ? '#FCA5A5' : 'var(--border)',
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                              <button
                                title="Remove row"
                                onClick={() => deleteInvalidRow(i)}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  width: 30, height: 30, padding: 0, border: '1px solid var(--border)',
                                  borderRadius: 6, background: 'transparent', cursor: 'pointer',
                                  color: 'var(--text-secondary)', transition: 'background 0.15s, color 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6" /><path d="M14 11v6" />
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                          {hasError && (
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td
                                colSpan={4}
                                style={{ padding: '2px 10px 8px', lineHeight: 1.4 }}
                              >
                                <span style={{ color: '#DC2626', fontSize: 12 }}>
                                  {errors.general && errors.general}
                                  {!errors.general && [
                                    errors.email && 'Invalid email address.',
                                    errors.phone && 'Phone must be 6–15 digits.',
                                  ].filter(Boolean).join('  ')}
                                </span>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                className="btn btn-primary"
                disabled={!allFixedRowsValid || importingFixed}
                onClick={handleImportFixed}
                title={!allFixedRowsValid ? 'Fix all errors before importing' : ''}
              >
                {importingFixed
                  ? <span className="spinner" />
                  : `Import ${editableInvalidRows.length} Customer${editableInvalidRows.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map(i => (
                <tr key={i}>
                  <td><div style={{ height: 14, width: 100, background: 'var(--bg)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} /></td>
                  <td><div style={{ height: 14, width: 150, background: 'var(--bg)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} /></td>
                  <td><div style={{ height: 14, width: 110, background: 'var(--bg)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} /></td>
                  <td><div style={{ height: 14, width: 80, background: 'var(--bg)', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <div style={{ height: 32, width: 32, background: 'var(--bg)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                      <div style={{ height: 32, width: 32, background: 'var(--bg)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : customers.length === 0 ? (
        debouncedSearch ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No customers found</h3>
              <p>Try a different search term.</p>
            </div>
          </div>
        ) : null
      ) : (
        <>
          <div className="card table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td>{c.email || '—'}</td>
                    <td>{c.phone || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {formatDate(c.createdAt)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          title="Edit customer"
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, padding: 0, border: '1px solid var(--border)',
                            borderRadius: 6, background: 'transparent', cursor: 'pointer',
                            color: 'var(--text-secondary)', transition: 'background 0.15s, color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover, #f0f0f0)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                          onClick={() => {
                            const parsed = parsePhone(c.phone);
                            setForm({
                              name: c.name || '',
                              email: c.email || '',
                              phoneCountryCode: parsed.country,
                              phoneNumber: parsed.number,
                            });
                            setEditCustomer(c);
                            setFormError('');
                            setShowAdd(true);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          title="Delete customer"
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, padding: 0, border: '1px solid var(--border)',
                            borderRadius: 6, background: 'transparent', cursor: 'pointer',
                            color: 'var(--text-secondary)', transition: 'background 0.15s, color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                          onClick={() => setDeleteTarget(c)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 20 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Previous
              </button>
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                Page {page} of {totalPages} ({total} customer{total !== 1 ? 's' : ''})
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {!loading && customers.length === 0 && !debouncedSearch && (
        <div style={{ marginTop: 24 }}>
          <CsvGuide />
        </div>
      )}
    </div>
  );
}
